import { ref, computed } from 'vue'

// Google Analytics 4 types
interface GAConfig {
  measurement_id: string
  anonymize_ip?: boolean
  send_page_view?: boolean
  debug_mode?: boolean
}

interface EcommerceItem {
  item_id: string
  item_name: string
  category: string
  quantity: number
  price: number
  currency?: string
}

interface EcommercePurchase {
  transaction_id: string
  value: number
  currency: string
  items: EcommerceItem[]
  shipping?: number
  tax?: number
}

export function useAnalytics() {
  // Проверяем, находимся ли мы в контексте Vue
  let route: { path: string } | null = null

  // Проверяем, доступен ли Vue Router контекст
  if (typeof window !== 'undefined' && window.location) {
    route = {
      path: window.location.pathname,
    }
  } else {
    route = { path: '/' }
  }

  // State
  const isInitialized = ref(false)
  const isEnabled = ref(true)
  const userId = ref<string | null>(null)
  const sessionId = ref<string>('')
  const debugMode = ref(import.meta.env.DEV)

  // Configuration
  const config: GAConfig = {
    measurement_id: import.meta.env['VITE_GA_MEASUREMENT_ID'] || 'G-XXXXXXXXXX',
    anonymize_ip: true,
    send_page_view: true,
    debug_mode: debugMode.value,
  }

  // Computed
  const canTrack = computed(() => isInitialized.value && isEnabled.value)

  // Initialize Google Analytics
  const initializeAnalytics = async (): Promise<void> => {
    if (isInitialized.value || typeof window === 'undefined') return

    try {
      // Load gtag script
      const script = document.createElement('script')
      script.async = true
      script.src = `https://www.googletagmanager.com/gtag/js?id=${config.measurement_id}`
      document.head.appendChild(script)

      // Initialize gtag
      // Без onerror и таймаута недоступный gtag (блокировщик, CSP, нет сети) вешал бы
      // initializeFlags, а вместе с ним навигацию по всем маршрутам за feature flag
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('gtag script load timeout')), 5000)
        script.onload = () => {
          clearTimeout(timeout)
          resolve()
        }
        script.onerror = () => {
          clearTimeout(timeout)
          reject(new Error('gtag script failed to load'))
        }
      })

      // Configure gtag
      window.dataLayer = window.dataLayer || []
      window.gtag = function (...args) {
        window.dataLayer.push(args)
      }

      window.gtag('js', new Date())
      window.gtag('config', config.measurement_id, {
        anonymize_ip: config.anonymize_ip,
        send_page_view: config.send_page_view,
        debug_mode: config.debug_mode,
        custom_map: {
          custom_parameter_1: 'user_type',
          custom_parameter_2: 'company_type',
        },
      })

      // Generate session ID
      sessionId.value = generateSessionId()

      isInitialized.value = true
      console.log('Google Analytics initialized')
    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error)
    }
  }

  // Generate unique session ID
  const generateSessionId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  // Set user ID for tracking
  const setUserId = (id: string | null): void => {
    userId.value = id

    if (canTrack.value && id) {
      window.gtag('config', config.measurement_id, {
        user_id: id,
      })
    }
  }

  // Track page view
  const trackPageView = (page_path?: string, page_title?: string): void => {
    if (!canTrack.value) return

    const eventData = {
      page_path: page_path || route.path,
      page_title: page_title || document.title,
      page_location: window.location.href,
      user_id: userId.value,
      session_id: sessionId.value,
    }

    window.gtag('event', 'page_view', eventData)

    if (debugMode.value) {
      console.log('Page view tracked:', eventData)
    }
  }

  // Track custom event
  const trackEvent = (eventName: string, parameters: Record<string, unknown> = {}): void => {
    if (!canTrack.value) return

    const eventData = {
      ...parameters,
      user_id: userId.value,
      session_id: sessionId.value,
      timestamp: Date.now(),
    }

    window.gtag('event', eventName, eventData)

    if (debugMode.value) {
      console.log(`Event tracked: ${eventName}`, eventData)
    }
  }

  // E-commerce tracking
  const trackPurchase = (purchaseData: EcommercePurchase): void => {
    if (!canTrack.value) return

    window.gtag('event', 'purchase', {
      transaction_id: purchaseData.transaction_id,
      value: purchaseData.value,
      currency: purchaseData.currency || 'RUB',
      shipping: purchaseData.shipping || 0,
      tax: purchaseData.tax || 0,
      items: purchaseData.items.map((item) => ({
        item_id: item.item_id,
        item_name: item.item_name,
        category: item.category,
        quantity: item.quantity,
        price: item.price,
      })),
    })
  }

  const trackAddToCart = (item: EcommerceItem): void => {
    trackEvent('add_to_cart', {
      currency: item.currency || 'RUB',
      value: item.price * item.quantity,
      items: [item],
    })
  }

  const trackRemoveFromCart = (item: EcommerceItem): void => {
    trackEvent('remove_from_cart', {
      currency: item.currency || 'RUB',
      value: item.price * item.quantity,
      items: [item],
    })
  }

  const trackBeginCheckout = (items: EcommerceItem[], value: number): void => {
    trackEvent('begin_checkout', {
      currency: 'RUB',
      value,
      items,
    })
  }

  // Business-specific events
  const trackProductView = (
    productId: string,
    productName: string,
    category: string,
    price?: number,
  ): void => {
    trackEvent('view_item', {
      item_id: productId,
      item_name: productName,
      category,
      price,
      currency: 'RUB',
    })
  }

  const trackSearch = (searchTerm: string, category?: string): void => {
    trackEvent('search', {
      search_term: searchTerm,
      category: category || 'all',
    })
  }

  const trackRFQRequest = (requestId: string, productIds: string[], totalValue?: number): void => {
    trackEvent('rfq_request', {
      request_id: requestId,
      product_count: Array.isArray(productIds) ? productIds.length : 0,
      product_ids: Array.isArray(productIds) ? productIds.join(',') : '',
      estimated_value: totalValue,
    })
  }

  const trackLogin = (method: string = 'email'): void => {
    trackEvent('login', {
      method,
    })
  }

  const trackSignUp = (method: string = 'email'): void => {
    trackEvent('sign_up', {
      method,
    })
  }

  // Conversion tracking
  const trackConversion = (conversionName: string, value?: number): void => {
    trackEvent('conversion', {
      conversion_name: conversionName,
      value: value || 1,
      currency: 'RUB',
    })
  }

  // User engagement
  const trackEngagement = (engagementType: string, duration?: number): void => {
    trackEvent('user_engagement', {
      engagement_time_msec: duration || 0,
      engagement_type: engagementType,
    })
  }

  // Error tracking
  const trackError = (errorName: string, errorMessage: string, fatal: boolean = false): void => {
    trackEvent('exception', {
      description: errorMessage,
      fatal,
      error_name: errorName,
    })
  }

  // Performance tracking
  const trackTiming = (category: string, variable: string, value: number, label?: string): void => {
    trackEvent('timing_complete', {
      name: variable,
      value,
      event_category: category,
      event_label: label,
    })
  }

  // Custom dimensions
  const setCustomDimension = (index: number, value: string): void => {
    if (!canTrack.value) return

    window.gtag('config', config.measurement_id, {
      [`custom_map.custom_dimension_${index}`]: value,
    })
  }

  // Consent management
  const updateConsentMode = (consentSettings: Record<string, 'granted' | 'denied'>): void => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', consentSettings)
    }
  }

  const grantAnalyticsConsent = (): void => {
    updateConsentMode({
      analytics_storage: 'granted',
      ad_storage: 'granted',
    })
    isEnabled.value = true
  }

  const denyAnalyticsConsent = (): void => {
    updateConsentMode({
      analytics_storage: 'denied',
      ad_storage: 'denied',
    })
    isEnabled.value = false
  }

  // Debug utilities
  const getDebugInfo = () => {
    return {
      isInitialized: isInitialized.value,
      isEnabled: isEnabled.value,
      userId: userId.value,
      sessionId: sessionId.value,
      measurementId: config.measurement_id,
      debugMode: debugMode.value,
    }
  }

  // Enhanced e-commerce events
  const trackViewItemList = (items: EcommerceItem[], listName: string): void => {
    trackEvent('view_item_list', {
      item_list_name: listName,
      items,
    })
  }

  const trackSelectItem = (item: EcommerceItem, listName: string): void => {
    trackEvent('select_item', {
      item_list_name: listName,
      items: [item],
    })
  }

  return {
    // State
    isInitialized,
    isEnabled,
    userId,
    sessionId,
    canTrack,

    // Core methods
    initializeAnalytics,
    setUserId,
    trackPageView,
    trackEvent,

    // E-commerce
    trackPurchase,
    trackAddToCart,
    trackRemoveFromCart,
    trackBeginCheckout,
    trackViewItemList,
    trackSelectItem,

    // Business events
    trackProductView,
    trackSearch,
    trackRFQRequest,
    trackLogin,
    trackSignUp,

    // Engagement & Performance
    trackConversion,
    trackEngagement,
    trackError,
    trackTiming,

    // Configuration
    setCustomDimension,
    grantAnalyticsConsent,
    denyAnalyticsConsent,

    // Debug
    getDebugInfo,
  }
}

// Global type declarations
declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}
